import { buildMailBodyDocument } from "@/lib/message/mailBodyDocument";

export type MailBodyDocumentKey = {
  accountId: number;
  folder: string;
  uid: number;
};

type CacheEntry = {
  fingerprint: string;
  srcDoc: string;
};

const MAX_ENTRIES = 56;
const cache = new Map<string, CacheEntry>();
const BODY_SCHEME = "light";

/** Empreinte légère — évite de hasher des HTML de plusieurs Mo. */
export function mailBodyHtmlFingerprint(html: string): string {
  const trimmed = html.trim();
  const len = trimmed.length;
  if (len === 0) return "0";

  let hash = 2166136261;
  const sample =
    len <= 8192 ? trimmed : trimmed.slice(0, 4096) + trimmed.slice(len - 4096);

  for (let i = 0; i < sample.length; i++) {
    hash ^= sample.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return `${len}:${hash >>> 0}`;
}

function cacheKey(fingerprint: string, messageKey?: MailBodyDocumentKey): string {
  if (messageKey) {
    return `${messageKey.accountId}\0${messageKey.folder}\0${messageKey.uid}\0${BODY_SCHEME}`;
  }
  return `fp\0${BODY_SCHEME}\0${fingerprint}`;
}

function touchEntry(key: string, entry: CacheEntry): void {
  cache.delete(key);
  cache.set(key, entry);
}

function evictOldestIfNeeded(): void {
  if (cache.size < MAX_ENTRIES) return;
  const oldest = cache.keys().next().value;
  if (oldest) cache.delete(oldest);
}

/** Construit ou récupère le srcDoc iframe (sanitize, toujours clair). */
export function buildMailBodyDocumentCached(
  html: string,
  messageKey?: MailBodyDocumentKey
): string {
  const trimmed = html.trim();
  if (!trimmed) return "";

  const fingerprint = mailBodyHtmlFingerprint(trimmed);
  const key = cacheKey(fingerprint, messageKey);
  const hit = cache.get(key);

  if (hit && hit.fingerprint === fingerprint) {
    touchEntry(key, hit);
    return hit.srcDoc;
  }

  const srcDoc = buildMailBodyDocument(trimmed);
  evictOldestIfNeeded();
  cache.set(key, { fingerprint, srcDoc });
  return srcDoc;
}

export function clearMailBodyDocumentCacheForMessage(
  accountId: number,
  folder: string,
  uid: number
): void {
  for (const scheme of ["light", "dark"] as const) {
    cache.delete(`${accountId}\0${folder}\0${uid}\0${scheme}`);
  }
}

export function clearMailBodyDocumentCacheForAccount(accountId: number): void {
  const prefix = `${accountId}\0`;
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

export function clearMailBodyDocumentCache(): void {
  cache.clear();
}
